import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trash2, AlertCircle, Shield, User } from "lucide-react";
import { getUsers, deleteUser } from "../apinew";

export default function UserManagementAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hoverRowId, setHoverRowId] = useState(null);
  const [hoverDeleteId, setHoverDeleteId] = useState(null);

  const rawUser = localStorage.getItem("user");
  const currentUser = rawUser ? JSON.parse(rawUser) : null;
  const isAdmin = currentUser?.role === "admin";

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError("");
      const list = await getUsers();
      setUsers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Gagal load users:", err);
      setError(
        err.response?.data?.message || err.message || "Gagal memuat daftar user"
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm("Yakin ingin menghapus user ini?")) return;

    try {
      await deleteUser(id);
      await loadUsers();
    } catch (err) {
      console.error("Gagal hapus user:", err);
      alert(
        err.response?.data?.message || err.message || "Gagal menghapus user"
      );
    }
  };

  if (!isAdmin) {
    return (
      <div
        style={{
          padding: "24px",
          minHeight: "100vh",
          background: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            background: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #fee2e2",
            padding: "40px",
            textAlign: "center",
            maxWidth: "400px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          }}
        >
          <AlertCircle
            size={48}
            color="#dc2626"
            style={{ margin: "0 auto 16px" }}
          />
          <h3
            style={{
              fontSize: "20px",
              fontWeight: "600",
              color: "#0f172a",
              marginBottom: "8px",
            }}
          >
            Akses Ditolak
          </h3>
          <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
            Anda tidak memiliki akses ke halaman ini.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", minHeight: "100vh", background: "#f8fafc" }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ marginBottom: "24px" }}
      >
        <h2
          style={{
            fontSize: "24px",
            fontWeight: "700",
            color: "#0f172a",
            marginBottom: "4px",
          }}
        >
          Kelola User
        </h2>
        <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
          Mengelola dan menghapus user terdaftar
        </p>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: "#fef2f2",
            border: "1px solid #fee2e2",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "16px",
            color: "#991b1b",
            fontSize: "14px",
          }}
        >
          {error}
        </motion.div>
      )}

      {/* Users Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
      >
        <div
          style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0" }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#0f172a",
              margin: 0,
            }}
          >
            Data User Terdaftar ({users.length})
          </h3>
        </div>

        <div style={{ overflowX: "auto" }}>
          {loading ? (
            <div
              style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}
            >
              Memuat data...
            </div>
          ) : users.length === 0 ? (
            <div
              style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}
            >
              Belum ada user terdaftar
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "left",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    ID
                  </th>
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "left",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "left",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Email
                  </th>
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "left",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Role
                  </th>
                  <th
                    style={{
                      padding: "12px 24px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.user_id}
                    style={{
                      borderBottom: "1px solid #f1f5f9",
                      transition: "all 0.2s ease",
                      background:
                        hoverRowId === u.user_id ? "#f8fafc" : "transparent",
                      boxShadow:
                        hoverRowId === u.user_id
                          ? "0 1px 3px rgba(0,0,0,0.05)"
                          : "none",
                    }}
                    onMouseEnter={() => setHoverRowId(u.user_id)}
                    onMouseLeave={() => setHoverRowId(null)}
                  >
                    <td
                      style={{
                        padding: "16px 24px",
                        fontSize: "14px",
                        color: "#64748b",
                      }}
                    >
                      #{u.user_id}
                    </td>
                    <td
                      style={{
                        padding: "16px 24px",
                        fontSize: "14px",
                        color: "#0f172a",
                        fontWeight: "500",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <div
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#ffffff",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          {u.name?.charAt(0)?.toUpperCase() || "U"}
                        </div>
                        {u.name}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "16px 24px",
                        fontSize: "14px",
                        color: "#64748b",
                      }}
                    >
                      {u.email || "-"}
                    </td>
                    <td style={{ padding: "16px 24px" }}>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "4px 10px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "500",
                          background:
                            u.role === "admin" ? "#f3e8ff" : "#dbeafe",
                          color: u.role === "admin" ? "#6b21a8" : "#1e40af",
                        }}
                      >
                        {u.role === "admin" ? (
                          <Shield size={12} />
                        ) : (
                          <User size={12} />
                        )}
                        {u.role || "user"}
                      </div>
                    </td>
                    <td style={{ padding: "16px 24px", textAlign: "center" }}>
                      <button
                        onClick={() => handleDelete(u.user_id)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #fecaca",
                          borderRadius: "6px",
                          background:
                            hoverDeleteId === u.user_id ? "#fecaca" : "#fee2e2",
                          color:
                            hoverDeleteId === u.user_id ? "#7f1d1d" : "#991b1b",
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "13px",
                          fontWeight: "500",
                          transition: "all 0.2s ease",
                          boxShadow:
                            hoverDeleteId === u.user_id
                              ? "0 1px 3px rgba(239,68,68,0.35)"
                              : "none",
                        }}
                        onMouseEnter={() => setHoverDeleteId(u.user_id)}
                        onMouseLeave={() => setHoverDeleteId(null)}
                      >
                        <Trash2 size={14} />
                        Hapus
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </motion.div>
    </div>
  );
}
